import React, { useContext, useEffect, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useDispatch } from "react-redux";
import {
  Box,
  Container,
  Typography,
  Button,
  Paper,
  Divider,
  CircularProgress
} from "@mui/material";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import WhatsAppIcon from "@mui/icons-material/WhatsApp";
import {
  API_RESPONSE_CODE,
  KivuI18nContext,
  localizedPath,
  useKivunovaTranslation
} from "@kivunova/kivufrontendcommon";
import { getSuccessPath } from "../../constants/routes";
import {
  setSelectedPlanForCheckout,
  type SelectedPlanForCheckout
} from "../../store/slices/planSlice";
import AnimatedContainer from "../common/AnimatedContainer";
import {
  buildWhatsAppSupportUrl,
  subscriptionPendingPaymentWhatsAppMessage,
  SUPPORT_WHATSAPP_BASE_URL
} from "../../Utils/supportContact";
import { useGetSubscriptionDetail } from "../../hooks/api/useSubscription";
import { useRefreshMembershipAfterPayment } from "../../hooks/api/useRefreshMembershipAfterPayment";
import type { Subscription } from "../../Graphql/types/subscription";

function selectedPlanFromSubscription(
  subscription: Subscription
): SelectedPlanForCheckout {
  return {
    planId: subscription.planId,
    name: subscription.planName,
    description: null,
    isActive: subscription.status === "ACTIVE",
    createdAt: subscription.createdAt,
    updatedAt: subscription.updatedAt,
    price: subscription.priceAmount ?? 0,
    currency: subscription.priceCurrency ?? "RWF",
    billingCycle: subscription.billingCycleCode,
    billingCycleId: subscription.billingCycleId
  };
}

const SubscriptionPartialSuccess: React.FC = () => {
  const { subscriptionId: subscriptionIdParam } = useParams<{
    subscriptionId: string;
  }>();
  const navigate = useNavigate();
  const { t } = useKivunovaTranslation();
  const { language, defaultLang } = useContext(KivuI18nContext) || {};
  const dispatch = useDispatch();
  const { mutateAsync: fetchSubscriptionDetail, isPending } =
    useGetSubscriptionDetail();
  const { refreshMembershipAfterPayment } = useRefreshMembershipAfterPayment();
  const redirectedRef = useRef(false);
  const [accessChecked, setAccessChecked] = useState(false);
  const fetchSubscriptionDetailRef = useRef(fetchSubscriptionDetail);
  const refreshMembershipAfterPaymentRef = useRef(
    refreshMembershipAfterPayment
  );
  const navigateRef = useRef(navigate);
  const dispatchRef = useRef(dispatch);
  const localeRef = useRef({ language, defaultLang });

  fetchSubscriptionDetailRef.current = fetchSubscriptionDetail;
  refreshMembershipAfterPaymentRef.current = refreshMembershipAfterPayment;
  navigateRef.current = navigate;
  dispatchRef.current = dispatch;
  localeRef.current = { language, defaultLang };

  const membershipId = subscriptionIdParam?.trim() ?? "";

  useEffect(() => {
    return () => {
      if (!redirectedRef.current) {
        dispatch(setSelectedPlanForCheckout(null));
      }
    };
  }, [dispatch]);

  useEffect(() => {
    if (!membershipId) {
      setAccessChecked(true);
      return;
    }

    let cancelled = false;
    setAccessChecked(false);

    void (async () => {
      try {
        const subscriptionRes = await fetchSubscriptionDetailRef.current({
          subscriptionId: membershipId
        });

        if (cancelled) {
          return;
        }

        const subscription =
          subscriptionRes.getSubscription?.code === API_RESPONSE_CODE.OK
            ? subscriptionRes.getSubscription.subscription
            : null;

        if (subscription?.status !== "ACTIVE" || redirectedRef.current) {
          return;
        }

        redirectedRef.current = true;
        const { language: lang, defaultLang: defaultLanguage } =
          localeRef.current;
        dispatchRef.current(
          setSelectedPlanForCheckout(selectedPlanFromSubscription(subscription))
        );
        navigateRef.current(
          localizedPath(getSuccessPath(membershipId), lang, defaultLanguage),
          {
            replace: true,
            state: {
              fromCompletedPayment: true
            }
          }
        );
        void refreshMembershipAfterPaymentRef.current().catch(() => undefined);
      } catch {
        // Stay on partial success when subscription is not active yet.
      } finally {
        if (!cancelled && !redirectedRef.current) {
          setAccessChecked(true);
        }
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [membershipId]);

  const whatsAppUrl = membershipId
    ? buildWhatsAppSupportUrl(
        subscriptionPendingPaymentWhatsAppMessage(membershipId)
      )
    : "";

  const showWhatsApp = Boolean(SUPPORT_WHATSAPP_BASE_URL && whatsAppUrl);
  const showLoader = Boolean(membershipId) && (!accessChecked || isPending);

  if (showLoader) {
    return (
      <Box
        sx={{
          minHeight: "100vh",
          background:
            "linear-gradient(135deg, #667eea 0%, #764ba2 25%, #6f49c9 50%, #5b36b0 75%, #4c2a85 100%)",
          display: "flex",
          alignItems: "center",
          justifyContent: "center"
        }}
      >
        <CircularProgress sx={{ color: "white" }} />
      </Box>
    );
  }

  return (
    <Box
      sx={{
        minHeight: "100vh",
        background:
          "linear-gradient(135deg, #667eea 0%, #764ba2 25%, #6f49c9 50%, #5b36b0 75%, #4c2a85 100%)",
        py: 6,
        px: { xs: 2, sm: 3, md: 4 },
        display: "flex",
        alignItems: "center",
        justifyContent: "center"
      }}
    >
      <Container maxWidth="md">
        <AnimatedContainer
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
        >
          <Paper
            elevation={8}
            sx={{
              borderRadius: 3,
              p: { xs: 3, sm: 4, md: 6 },
              background: "rgba(255, 255, 255, 0.95)",
              backdropFilter: "blur(10px)"
            }}
          >
            <Box sx={{ textAlign: "center", mb: 4 }}>
              <Box
                sx={{
                  display: "inline-flex",
                  alignItems: "center",
                  justifyContent: "center",
                  width: 44,
                  height: 44,
                  borderRadius: "50%",
                  background:
                    "linear-gradient(135deg, #ff9800 0%, #f57c00 100%)",
                  mb: 3,
                  boxShadow: "0 6px 12px rgba(255, 152, 0, 0.35)"
                }}
              >
                <InfoOutlinedIcon sx={{ fontSize: 24, color: "white" }} />
              </Box>
              <Typography
                fontWeight="bold"
                sx={{
                  fontSize: { xs: "1.35rem", sm: "1.75rem", md: "2rem" },
                  mb: 2
                }}
              >
                {t(
                  "subscription-partial-success-title",
                  "Subscription created — payment pending"
                )}
              </Typography>
              <Typography
                variant="body1"
                color="text.secondary"
                sx={{
                  fontSize: { xs: "0.875rem", sm: "0.9375rem" },
                  lineHeight: 1.7,
                  fontWeight: 400,
                  maxWidth: 560,
                  mx: "auto"
                }}
              >
                {t(
                  "subscription-partial-success-message",
                  "Your subscription has been created successfully, but payment could not be completed at this time. Our admin team has been notified."
                )}
              </Typography>
            </Box>

            {showWhatsApp ? (
              <>
                <Divider sx={{ my: 3 }} />
                <Box
                  sx={{
                    mb: 3,
                    p: 2.5,
                    borderRadius: 2,
                    bgcolor: "rgba(37, 211, 102, 0.08)",
                    border: "1px solid rgba(37, 211, 102, 0.35)",
                    textAlign: "center"
                  }}
                >
                  <Typography
                    variant="subtitle1"
                    fontWeight="bold"
                    sx={{ mb: 1 }}
                  >
                    {t(
                      "subscription-partial-success-whatsapp-title",
                      "Contact admin directly"
                    )}
                  </Typography>
                  <Typography
                    variant="body2"
                    color="text.secondary"
                    sx={{ lineHeight: 1.7, mb: 2, maxWidth: 480, mx: "auto" }}
                  >
                    {t(
                      "subscription-partial-success-whatsapp-details",
                      "For faster help, click below to open WhatsApp and message our admin team directly. Your membership ID is included automatically so we can review your subscription and payment right away."
                    )}
                  </Typography>
                  <Button
                    component="a"
                    href={whatsAppUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    variant="contained"
                    fullWidth
                    startIcon={<WhatsAppIcon />}
                    sx={{
                      backgroundColor: "#25D366",
                      py: 1.5,
                      borderRadius: 2,
                      textTransform: "none",
                      fontWeight: "bold",
                      fontSize: "1rem",
                      maxWidth: 420,
                      mx: "auto",
                      display: "flex",
                      "&:hover": { backgroundColor: "#1ebe57" }
                    }}
                  >
                    {t(
                      "subscription-partial-success-whatsapp-btn",
                      "Contact admin on WhatsApp"
                    )}
                  </Button>
                </Box>
              </>
            ) : null}
          </Paper>
        </AnimatedContainer>
      </Container>
    </Box>
  );
};

export default SubscriptionPartialSuccess;
